// src/app/components/SphereDetails/SphereDetails.tsx

import React, { useEffect, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import PrimeReactSingleSelect from '../../../atoms/single-select-dropdown/single-select-dropdown';
import { useApiClientsOrNull } from '../../../../context/ApiClientsContext';
import { useAvailableTypes } from '../../../../hooks/useAvailableTypes';
import './atlas-spheresdetails.scss';
import { PermissionList } from '../../../../../types/sharedTypes';
import { updateUrgency } from '@center-platform/spheres/sphere';
import '../../../atoms/urgency/urgency.scss';
import { Sphere } from 'types/global';
import type { GlyphColor } from '../../../../types/glyph';

interface SphereDetailsProps {
  nodeLabel: string;
  nodedescription: string;
  nodelastUpdated: string;
  nodecreated: string;
  sphereId: string;
  urgency: string;
  nodeType: string;
  orgId: string;
  permissionList: PermissionList;
  parent: Sphere;
  connectingPerspectives: Array<{
    urgency?: number;
    edgeId?: string;
  }>;
}

const SphereDetails: React.FC<SphereDetailsProps> = ({
  nodeLabel,
  nodedescription,
  nodelastUpdated,
  nodecreated,
  sphereId,
  urgency,
  nodeType,
  orgId,
  permissionList,
  parent,
  connectingPerspectives,
}) => {
  const apiClients = useApiClientsOrNull();
  const { schemas: availableSchemas } = useAvailableTypes(apiClients, sphereId);

  if (!apiClients) {
    return null;
  }

  const { graphClient } = apiClients;
  const [value, setValue] = useState<string>(nodeLabel);
  const [description, setDescription] = useState<string>(nodedescription);
  const [lastUpdated, setLastUpdated] = useState<string>('');
  const [created, setCreated] = useState<string>('');
  const [perspectiveDetails, setPerspectiveDetails] = useState<any>(null);
  const [parentOptions, setParentOptions] = useState<
    Array<{ name: string; code: string; glyph?: string; glyphColor?: GlyphColor }>
  >([]);
  const [selectedType, setSelectedType] = useState<{
    name: string;
    code: string;
    system_name?: string;
  } | null>(null);
  const [selectedParent, setSelectedParent] = useState<{
    name: string;
    code: string;
    glyph?: string;
    glyphColor?: GlyphColor;
  } | null>(null);
  const [selectedUrgency, setSelectedUrgency] = useState<{
    name: string;
    code: string;
  } | null>(null);
  const [edgeId, setEdgeId] = useState<string>('');

  const { view, edit } = permissionList.permissions;

  // Urgency dropdown options
  const urgentOptions = [
    { name: 'Urgent', code: '5' },
    { name: 'High', code: '4' },
    { name: 'Medium', code: '3' },
    { name: 'Low', code: '2' },
    { name: 'Parked', code: '1' },
  ];

  // Type dropdown options from the sphere's deployed approaches (no Redux fallback).
  const typeOptions = availableSchemas
    .filter((schema: any) => !schema.is_abstract)
    .map((schema: any) => ({
      name: schema.name,
      code: schema.schema_id,
      glyph: schema.glyph || 'default-icon',
      glyphColor: schema.glyph_color ?? undefined,
      system_name: schema.system_name,
    }));

  // Format dates on mount
  useEffect(() => {
    const fmt = (iso: string) => new Date(iso).toLocaleString();
    setLastUpdated(fmt(nodelastUpdated));
    setCreated(fmt(nodecreated));
  }, [nodelastUpdated, nodecreated]);

  // When connectingPerspectives arrives, pick first to seed urgency and edgeId
  useEffect(() => {
    if (connectingPerspectives.length > 0) {
      const first = connectingPerspectives[0];
      const urg = first.urgency ?? 0;
      const eId = first.edgeId ?? '';
      setEdgeId(eId);
      const match = urgentOptions.find((o) => Number(o.code) === urg);
      setSelectedUrgency(match || null);
    }
  }, [connectingPerspectives]);

  // Fetch sphere info → properties for updatedBy name
  const fetchPerspectiveDetails = async (id: string) => {
    try {
      const resp = await graphClient.get<{ sphere: { properties: any } }>(
        `/spheres/${encodeURIComponent(id)}/info`
      );
      setPerspectiveDetails(resp.sphere.properties);
    } catch (err) {
      console.error('Error fetching perspective details:', err);
    }
  };

  useEffect(() => {
    if (sphereId) fetchPerspectiveDetails(sphereId);
  }, [sphereId]);

  // Fetch parent dropdown data based on selectedType
  const fetchParentData = async () => {
    if (!selectedType) return;
    try {
      const body = {
        schemaSystemName: selectedType.system_name,
        perspectiveSystemName: 'center_parent',
        limit: 50,
        page: 1,
      };
      const resp = await graphClient.post<{
        spheres: Sphere[];
      }>(`/search/potential-assignees`, body);
      const formatted = resp.spheres.map((s) => ({
        name: s.properties.name,
        code: s.id,
        glyph: s.glyph,
        glyphColor: s.glyphColor,
      }));
      setParentOptions(formatted);
    } catch (err) {
      console.error('Error fetching parent data:', err);
    }
  };

  // When nodeType or typeOptions change, set selectedType and trigger parent load
  useEffect(() => {
    if (!selectedType && nodeType && typeOptions.length > 0) {
      const match = typeOptions.find((t) => t.system_name === nodeType);
      if (match) {
        setSelectedType(match);
      }
    }
  }, [nodeType, typeOptions, selectedType]);

  // Parent data is fetched on demand when the dropdown is opened, not eagerly on mount

  // When `parent` prop arrives, pre-select in dropdown
  useEffect(() => {
    if (parent && parentOptions.length > 0) {
      const match = parentOptions.find((o) => o.code === parent.id);
      if (match) setSelectedParent(match);
    }
  }, [parent, parentOptions]);

  // Handle urgency change
  const handleUrgencyChange = async (code: string) => {
    if (!edgeId) return;
    try {
      // you might replace updateUrgency with a graphClient.put
      await updateUrgency(graphClient, { edgeId, urgencyCode: code });
      setSelectedUrgency(
        urgentOptions.find((o) => o.code === code) || null
      );
    } catch (err) {
      console.error('Error updating urgency:', err);
    }
  };

  if (!view) {
    return null;
  }

  return (
    <div className="sphere-details-container">
      <div className="columns-container">
        <div className="left-column">
          <div className="dropdown-container" style={{ display: 'flex', gap: '0.5rem' }}>
            <PrimeReactSingleSelect
              value={selectedType || { name: '', code: '' }}
              options={typeOptions}
              onChange={(e) => {
                if (!edit) return;
                setSelectedType(e.value);
              }}
              placeholder="Select Type"
              optionLabel="name"
              filter
              disabled={!edit}
            />
            <PrimeReactSingleSelect
              value={selectedParent || { name: '', code: '' }}
              options={parentOptions}
              onChange={(e) => {
                if (!edit) return;
                setSelectedParent(e.value);
              }}
              onShow={fetchParentData}
              placeholder="Select Parent"
              optionLabel="name"
              filter
              disabled={!edit}
            />
            <PrimeReactSingleSelect
              value={selectedUrgency || { name: '', code: '' }}
              options={urgentOptions}
              onChange={(e) => {
                if (!edit) return;
                const opt = e.value;
                if (opt) {
                  handleUrgencyChange(opt.code);
                }
              }}
              placeholder="Select Urgency"
              optionLabel="name"
              disabled={!edit}
              className="urgency-dropdown"
            />
          </div>
        </div>
        <div className="right-column pt-4">
          <div className="left">
            <div className="title-editable">
              <InputText
                value={value}
                onChange={(e) => setValue(e.target.value)}
                disabled={!edit}
                className="spheredetails-search"
                style={{
                  border: edit ? '1px solid var(--secondary-color)' : 'none',
                }}
              />
            </div>
            <div className="description-section">
              <InputTextarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={5}
                cols={30}
                disabled={!edit}
                style={{
                  border: edit ? '1px solid var(--secondary-color)' : 'none',
                }}
              />
            </div>
          </div>
          <div className="right">
            <div className="parent-info">
              <p><strong>Last Update:</strong> <span>{lastUpdated}</span></p>
              <p><strong>Updated By:</strong> <span className="username">{perspectiveDetails?.name}</span></p>
              <p><strong>Creation:</strong> <span>{created}</span></p>
              <p><strong>Created By:</strong> <span className="username">{perspectiveDetails?.name}</span></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SphereDetails;
